import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Sparkles, ArrowRight, Lock, Clock } from "lucide-react";

interface TrialInstructionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  daysRemaining?: number;
  onSeePlans?: () => void;
}

export function TrialInstructionsModal({
  open,
  onOpenChange,
  daysRemaining = 7,
  onSeePlans,
}: TrialInstructionsModalProps) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const handleClose = () => {
    localStorage.setItem("academy_trial_instructions_seen", "true");
    onOpenChange(false);
  };

  const handleNext = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else handleClose();
  };

  const steps = [
    {
      icon: Sparkles,
      badge: "Bem-vindo!",
      title: `Seu trial de ${daysRemaining} dias começou 🎉`,
      description:
        "Você tem 7 dias para experimentar a Academy antes de decidir se quer assinar. Sem cartão, sem cobrança automática.",
      content: (
        <div className="rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 p-4 border border-primary/20">
          <div className="flex items-center gap-3">
            <Clock className="h-8 w-8 text-primary" />
            <div>
              <p className="text-sm font-medium">Duração do trial</p>
              <p className="text-2xl font-bold">{daysRemaining} dias</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      icon: Check,
      badge: "O que você libera no trial",
      title: "Acesso ao 1º módulo de cada trilha",
      description:
        "Você pode estudar o primeiro módulo completo de Power BI, SQL e Python — aula + exercícios + prova. É a melhor forma de sentir a experiência.",
      content: (
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>1º módulo de <strong>Power BI</strong> (Introdução ao Power BI)</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>1º módulo de <strong>SQL</strong> (Fundamentos de SQL)</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>1º módulo de <strong>Python</strong> (Python para Análise de Dados)</span>
          </li>
          <li className="flex items-start gap-2">
            <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>Histórico de tentativas e ranking geral</span>
          </li>
        </ul>
      ),
    },
    {
      icon: Lock,
      badge: "O que exige assinatura PRO",
      title: "Recursos avançados só para assinantes",
      description:
        "Ao assinar, você desbloqueia todos os módulos avançados e as ferramentas de IA para acelerar sua carreira.",
      content: (
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <Crown className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <span><strong>Todos os módulos</strong> das 3 trilhas (avançado + prova)</span>
          </li>
          <li className="flex items-start gap-2">
            <Crown className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <span><strong>Simulador de entrevista</strong> com IA</span>
          </li>
          <li className="flex items-start gap-2">
            <Crown className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <span><strong>Analisador de currículo</strong> com feedback IA</span>
          </li>
          <li className="flex items-start gap-2">
            <Crown className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <span><strong>Certificados</strong> verificáveis publicamente</span>
          </li>
          <li className="flex items-start gap-2">
            <Crown className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <span><strong>Tutor IA</strong> sem limite diário</span>
          </li>
        </ul>
      ),
    },
    {
      icon: ArrowRight,
      badge: "Pronto?",
      title: "Comece pelo diagnóstico",
      description:
        "Escolha uma trilha e responda 3 perguntas rápidas. Sugerimos o melhor ponto de partida para você.",
      content: (
        <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4 text-center">
          <p className="text-sm text-muted-foreground">
            💡 Dica: comece pelo <strong className="text-foreground">SQL</strong> se você é novo em dados. É a base de tudo.
          </p>
        </div>
      ),
    },
  ];

  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <Badge className="w-fit bg-primary/10 text-primary border border-primary/20 mb-1">
            {current.badge}
          </Badge>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Icon className="h-5 w-5 text-primary" />
            {current.title}
          </DialogTitle>
          <DialogDescription className="text-base">
            {current.description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">{current.content}</div>

        <div className="flex justify-center gap-1.5 pb-2">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"
              }`}
              aria-label={`Etapa ${i + 1}`}
            />
          ))}
        </div>

        <div className="flex gap-3">
          {isLast && onSeePlans ? (
            <Button
              variant="outline"
              className="flex-1 gap-1.5"
              onClick={() => {
                handleClose();
                onSeePlans();
              }}
            >
              <Crown className="h-4 w-4 text-amber-500" />
              Ver planos PRO
            </Button>
          ) : (
            <Button variant="ghost" className="flex-1" onClick={handleClose}>
              Pular
            </Button>
          )}
          <Button className="flex-1 gap-1.5" onClick={handleNext}>
            {isLast ? "Começar a estudar" : "Próximo"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
